import { Eye, MousePointerClick, ShieldCheck } from 'lucide-react';

import type { MedallionHue } from '@/components/bedrock';
import type { LucideIcon } from 'lucide-react';

/**
 * `/how-it-works` — the three steps, and the three things a reader wants
 * checked off before they will believe any of it.
 *
 * Structure only. Every visitor-facing string is a `howItWorks.*` key resolved
 * through `useT()` at the call site, so the page is complete in all six locales
 * or it is not shipped (`quality-bar.md` § "Multilingual Is Part of Done").
 *
 * ## Why the page exists separately from the landing's `#how` band
 *
 * The landing band is a 40-second summary with a sliding stage: it shows the
 * install, one screen of what gets read, and one live conversation. It answers
 * "is this plausible". It cannot answer "what exactly happens in hour one, and
 * what is it allowed to do afterwards" without becoming the whole page — which
 * is what this is. The nav now points at this; the landing band is the trailer.
 */

export interface HowStep {
  /** Stable slug — the React key, the `data-step` hook, and the anchor id. */
  readonly id: string;
  readonly icon: LucideIcon;
  readonly hue: MedallionHue;
  /** Number of `howItWorks.step.<id>.detail.N` bullet keys the step renders. */
  readonly detailCount: number;
}

/**
 * Three steps, and the ORDER is the argument.
 *
 * Most products in this category open with configuration, because configuration
 * is what they need from you. The order here is install → it reads → you
 * approve, because that is the sequence a customer actually experiences and
 * because the approval step lands AFTER the reader has seen how much was found.
 * Putting governance first reads as a warning; putting it third reads as the
 * answer to the question the second step just raised.
 */
export const HOW_STEPS: readonly HowStep[] = [
  { id: 'install', icon: MousePointerClick, hue: 'cyan', detailCount: 3 },
  { id: 'learn', icon: Eye, hue: 'blue', detailCount: 5 },
  { id: 'approve', icon: ShieldCheck, hue: 'violet', detailCount: 4 },
];

/**
 * The control grid — six properties that hold for every action the agent takes,
 * on every surface, whether or not anyone configured anything.
 *
 * These are the objections a buyer raises in the order they raise them: who is
 * it acting as, can it do something I did not intend, can I prove what it did,
 * who decides what it can reach, where does the data go, and how do I stop it.
 * Answering them in that order is why the section reads as reassurance rather
 * than as a feature list.
 */
/**
 * A literal union rather than `string`, so the icon map in `HowItWorks.tsx` is
 * exhaustive by TYPE. With `string` the lookup is `LucideIcon | undefined` and
 * a fact added here without an icon renders nothing at all — a silent hole in
 * a grid, which is the failure a `Record` keyed on the union cannot have.
 */
export type ControlFactId = 'identity' | 'confirm' | 'audit' | 'scope' | 'residency' | 'killSwitch';

export interface ControlFact {
  readonly id: ControlFactId;
  readonly hue: MedallionHue;
}

export const CONTROL_FACTS: readonly ControlFact[] = [
  { id: 'identity', hue: 'blue' },
  { id: 'confirm', hue: 'cyan' },
  { id: 'audit', hue: 'violet' },
  { id: 'scope', hue: 'purple' },
  { id: 'residency', hue: 'ok' },
  { id: 'killSwitch', hue: 'warn' },
];
